import { forwardRef, memo, useCallback, useImperativeHandle, useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

import { HorizontalContainer, Wrapper } from '@american-autonomy/ag-ui';

import LinkButton from 'components/LinkButton';

import useDateRangePicker from './hooks/useDateRangePicker';
import useTranslation from './hooks/useTranslation';

import DateRangePickerInputs from './components/DateRangePickerInputs';
import { TEST_ID } from './constants';
import type { Props, RangeRefs } from './types';

import styles from './styles.module.scss';

const DateRangePicker = memo(
    forwardRef<RangeRefs, Props>(
        ({ className = '', onChange, rangeInterval, testId = TEST_ID, value }, ref) => {
            const { RESET } = useTranslation();

            const startInputRef = useRef<HTMLInputElement | null>(null);
            const endInputRef = useRef<HTMLInputElement | null>(null);
            const popoverRef = useRef<HTMLDivElement | null>(null);

            const inputRefs: RangeRefs = {
                endInputRef,
                startInputRef
            };

            useImperativeHandle(ref, () => inputRefs);

            const {
                containerRef,
                dayPickerInterval,
                disableDaysFromRange,
                endInputValue,
                handleBlur,
                handleChange,
                handleOpen,
                handleTextChange,
                reset,
                selectedDateRange,
                startInputValue
            } = useDateRangePicker({
                onChange,
                popoverRef,
                rangeInterval,
                refs: inputRefs,
                value
            });

            const handleStartOpen = useCallback(() => {
                handleOpen('start');
            }, [handleOpen]);

            const handleEndOpen = useCallback(() => {
                handleOpen('end');
            }, [handleOpen]);

            return (
                <Wrapper className={`${className} ${styles.dateRangePicker}`} testId={testId}>
                    <Wrapper className={styles.inputsContainer} ref={containerRef}>
                        <DateRangePickerInputs
                            endValue={endInputValue}
                            onBlur={handleBlur}
                            onChange={handleTextChange}
                            onEndClick={handleEndOpen}
                            onStartClick={handleStartOpen}
                            refs={inputRefs}
                            startValue={startInputValue}
                            testId={testId}
                        />
                    </Wrapper>

                    <div
                        className={styles.dropdown}
                        data-testid={`${testId}-dropdown`}
                        popover='manual'
                        ref={popoverRef}>
                        <DayPicker
                            defaultMonth={selectedDateRange.to ? selectedDateRange.to : undefined}
                            disabled={disableDaysFromRange}
                            endMonth={rangeInterval.end as Date}
                            hidden={dayPickerInterval}
                            mode='range'
                            onSelect={handleChange}
                            selected={selectedDateRange}
                            startMonth={rangeInterval.start as Date}
                        />

                        <HorizontalContainer className={styles.footer}>
                            <LinkButton onClick={reset} text={RESET} />
                        </HorizontalContainer>
                    </div>
                </Wrapper>
            );
        }
    )
);

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
